import React, { useRef, useState } from 'react'

import widgetsService from 'live/services/widgets'

import { useComponentSize, useFullWidget } from '@commons/hooks'
import { flow, setEl, destroy } from '@commons/widget-helper/groupedLiveWidget/functions'
import {
    Container,
    Horizontal,
    Chart,
    ValueConfiguration,
    WidthConfiguration
} from '@commons/widget-helper/groupedLiveWidget/HelperComponents'
import LiveChart from '@commons/widget-helper/groupedLiveWidget/LiveChart'
import ReactBase from '@digital-widgets/widget-helper/src/ReactBase'
import styled from 'styled-components'

class ValueXrange2Widget extends ReactBase {
    constructor(props) {
        super(props)
        this.props = props

        this.leftRef = React.createRef()
        this.rightRef = React.createRef()

        this.flow = flow
        this.setEl = setEl
        this.destroy = destroy
        this.widgetContext = props.context
    }

    flow(type, events, layer) {
        if (layer === 0) {
            if (type === 'start') {
                this.lastEvent = {}
            } else {
                const newEvent = events[events.length - 1]
                this.message = { type, events }
                if (!newEvent || !newEvent.items) {
                    return
                }

                this.lastEvent = newEvent
            }
        } else {
            if (!this.rightRef.current) {
                console.warn('rightRef.current null but received event', type, events)
            } else {
                this.rightRef.current.flow(type, events, layer)
            }
        }
    }

    render() {
        this.onLoad()

        return () => {
            const ref = useRef(null)
            const { visibleWidth } = useComponentSize(ref)
            useFullWidget(ref)

            const {
                valueWidth = 30,
                valueMaxWidth = 260,
                xrangeWidth = 70,
                suffixBottom,
                prefix,
                suffix,
                fractionDigits = 2,
                properties
            } = this.props.context.widget.jsonConfig

            const timespan = ref.current && $(ref.current).closest('.widget-container').find('.widget-description')[0]
            if (timespan && visibleWidth) {
                timespan.style.maxWidth = Math.min(valueMaxWidth, (valueWidth * visibleWidth) / 100) + 'px'
            }

            return (
                <DivContainer ref={ref}>
                    <Container>
                        <Horizontal>
                            <Chart
                                style={{ display: 'flex', position: 'sticky', top: 0 }}
                                value={true}
                                timespan={timespan}
                                widthPct={valueWidth}
                                maxWidth={valueMaxWidth}
                                suffixBottom={suffixBottom}
                            >
                                <LiveChart
                                    type="value"
                                    className="widget-content-container"
                                    {...this.props}
                                    prefix={prefix}
                                    suffix={suffix}
                                    suffixBottom={suffixBottom}
                                    fractionDigits={fractionDigits}
                                    style={valueWidth === 0 ? { display: 'none' } : {}}
                                    ref={this.leftRef}
                                    layers={[0]}
                                    totalLayers={properties.length}
                                />
                            </Chart>
                            <Chart
                                timespan={timespan}
                                widthPct={xrangeWidth}
                                leftWidthPct={valueWidth}
                                fullWidth={visibleWidth}
                                maxWidth={valueMaxWidth}
                                minWidth={true}
                            >
                                <LiveChart
                                    type="xrange2"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        display: xrangeWidth === 0 ? 'none' : 'block'
                                    }}
                                    {...this.props}
                                    config={this.props.cfg}
                                    totalLayers={properties.length}
                                    ref={this.rightRef}
                                    properties={properties}
                                    widgetContext={this.widgetContext}
                                />
                            </Chart>
                        </Horizontal>
                    </Container>
                </DivContainer>
            )
        }
    }
}

const DivContainer = styled.div`
    width: 100%;
    height: 100%;
    .widget-content-container {
        #value-widget-content {
            display: flex;
            flex-direction: column;
        }
    }
`

const Config = props => {
    const [suffix, setSuffix] = useState(props.config.suffix)
    const [prefix, setPrefix] = useState(props.config.prefix)
    const [suffixBottom, setSuffixBottom] = useState(props.config.suffixBottom)
    const [fractionDigits, setFractionDigits] = useState(props.config.fractionDigits)
    const [valueWidth, setValueWidth] = useState(props.config.valueWidth)
    const [xrangeWidth, setXrangeWidth] = useState(props.config.xrangeWidth)
    const [valueMaxWidth, setValueMaxWidth] = useState(props.config.valueMaxWidth)

    return (
        <>
            <ValueConfiguration
                prefix={prefix}
                setPrefix={setPrefix}
                suffix={suffix}
                setSuffix={setSuffix}
                suffixBottom={suffixBottom}
                setSuffixBottom={setSuffixBottom}
                fractionDigits={fractionDigits}
                setFractionDigits={setFractionDigits}
                onChange={props.onChange}
            />

            <WidthConfiguration
                leftWidth={valueWidth}
                setLeftWidth={setValueWidth}
                leftMaxWidth={valueMaxWidth}
                setLeftMaxWidth={setValueMaxWidth}
                leftChart="value"
                rightWidth={xrangeWidth}
                setRightWidth={setXrangeWidth}
                rightChart="xrange2"
                onChange={props.onChange}
            />
        </>
    )
}

widgetsService.name('value-xrange2', 'Value & XRange 2')

// Notice on disableLatestOptimization:
// This @meta prevents the queries from trimming the preload windows. The preload is important in @xrange functions, because they allow to build an initial state before the start of user span
// Trick: the metadata in the reducer does not actually work (it is indeed known to the Pipes engine, but should be inside the main query).
//        adding it to the Reducer works in an indirect way: any reducer different from '@latest' will not trigger the Preload optimization.
//        I.e., we could add any String to the reducer expression, as long as it does not alter the @latest result. This one acts as a "purpose documentation" for changing the normal '@latest' reducer
widgetsService.policies('value-xrange2', { defaultReducer: '@latest => @meta true as disableLatestOptimization' })

widgetsService.properties('value-xrange2', widgetsService.properties('xrange2'))
widgetsService.impl('value-xrange2', ValueXrange2Widget)
widgetsService.config('value-xrange2', {
    ...widgetsService.config('xrange2'),
    ...widgetsService.config('value'),
    suffix: '',
    prefix: '',
    suffixBottom: true,
    valueWidth: 30,
    xrangeWidth: 70,
    valueMaxWidth: 260,
    fractionDigits: 2,
    snapshot: false
})
widgetsService.ui('value-xrange2', {
    configForm: Config
})
